import { create } from 'zustand';
import { supabase } from '../lib/supabase';

/** Evita tormentas de red cuando varios efectos llaman fetchSubscriptions al mismo tiempo. */
const fetchSubscriptionsInFlight = new Map<string, Promise<void>>();
const fetchLastAttendanceInFlight = new Map<string, Promise<void>>();
const getPaymentsByMonthInFlight = new Map<string, Promise<unknown[]>>();
const updatePaymentStatusesInFlight = new Map<string, Promise<void>>();

export interface MemberSubscription {
  id: string;
  empresa_id: string;
  cliente_id: string;
  producto_id: string;
  status: 'active' | 'suspended' | 'cancelled' | 'expired';
  start_date: string;
  current_period_start: string;
  current_period_end: string;
  next_billing_date: string | null;
  cancelled_at: string | null;
  classes_remaining: number | null;
  total_classes_used: number;
  auto_renew: boolean;
  payment_method_id: string | null;
  check_in_token?: string; // Deprecated: universal QR by slug; kept for legacy
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  // Payment tracking
  last_payment_date?: string | null;
  payment_status?: 'current' | 'due_soon' | 'overdue' | 'suspended';
  days_overdue?: number;
  overdue_amount?: number;

  // Joined data
  cliente?: Record<string, unknown>;
  producto?: Record<string, unknown>;
}

export interface AttendanceRecord {
  id: string;
  empresa_id: string;
  subscription_id: string;
  cliente_id: string;
  check_in_time: string;
  check_in_method: 'qr_self' | 'manual' | 'app' | 'kiosk';
  checked_in_by_user_id: string | null;
  notes: string | null;
  metadata: Record<string, unknown>;
  created_at: string;

  // Joined data
  cliente?: Record<string, unknown>;
  subscription?: MemberSubscription;
}

/** Last check-in per subscription (subscription_id -> latest record) for display on subscriptions page */
export type LastAttendanceMap = Record<string, { check_in_time: string; check_in_method: AttendanceRecord['check_in_method'] }>;

interface SubscriptionsStore {
  subscriptions: MemberSubscription[];
  attendanceRecords: AttendanceRecord[];
  /** Latest attendance per subscription_id for "last time they came" on subscriptions page */
  lastAttendanceBySubscriptionId: LastAttendanceMap;
  isLoading: boolean;

  fetchSubscriptions: (empresaId: string) => Promise<void>;
  fetchAttendance: (empresaId: string, dateFrom?: string, dateTo?: string) => Promise<void>;
  /** Fetches latest check-in per subscription (for subscriptions page "last came" column) */
  fetchLastAttendancePerSubscription: (empresaId: string) => Promise<void>;
  createSubscription: (data: Partial<MemberSubscription>) => Promise<MemberSubscription | null>;
  updateSubscription: (id: string, data: Partial<MemberSubscription>) => Promise<void>;
  recordAttendance: (data: Partial<AttendanceRecord>) => Promise<AttendanceRecord | null>;
  getSubscriptionByToken: (token: string) => Promise<MemberSubscription | null>;
  getActiveSubscriptions: (empresaId: string) => MemberSubscription[];
  getOverdueSubscriptions: (empresaId: string) => MemberSubscription[];
  getDueSoonSubscriptions: (empresaId: string) => MemberSubscription[];
  recordPayment: (subscriptionId: string, amount: number, paymentMethod: string) => Promise<void>;
  getPaymentsByMonth: (empresaId: string, year: number, month: number) => Promise<unknown[]>;
  updatePaymentStatuses: (empresaId: string) => Promise<void>;
  reset: () => void;
}

export const useSubscriptionsStore = create<SubscriptionsStore>((set, get) => ({
  subscriptions: [],
  attendanceRecords: [],
  lastAttendanceBySubscriptionId: {},
  isLoading: false,

  fetchSubscriptions: async (empresaId: string) => {
    const existing = fetchSubscriptionsInFlight.get(empresaId);
    if (existing) return existing;

    const run = (async () => {
      set({ isLoading: true });
      try {
        const { data, error } = await supabase
          .from('member_subscriptions')
          .select(
            `
          *,
          cliente:clientes(*),
          producto:productos(*)
        `
          )
          .eq('empresa_id', empresaId)
          .order('created_at', { ascending: false });

        if (error) throw error;
        set({ subscriptions: data || [], isLoading: false });
      } catch (err: unknown) {
        const message = err && typeof err === 'object' && 'message' in err ? (err as Error).message : String(err);
        const details =
          err && typeof err === 'object' && 'details' in err ? (err as { details?: string }).details : undefined;
        console.error('Error fetching subscriptions:', message, details ?? '');
        set({ subscriptions: [], isLoading: false });
      } finally {
        fetchSubscriptionsInFlight.delete(empresaId);
      }
    })();

    fetchSubscriptionsInFlight.set(empresaId, run);
    return run;
  },

  fetchAttendance: async (empresaId: string, dateFrom?: string, dateTo?: string) => {
    set({ isLoading: true });
    try {
      let query = supabase
        .from('attendance_records')
        .select(
          `
          *,
          cliente:clientes(*),
          subscription:member_subscriptions(*)
        `
        )
        .eq('empresa_id', empresaId)
        .order('check_in_time', { ascending: false });

      if (dateFrom) {
        query = query.gte('check_in_time', dateFrom);
      }
      if (dateTo) {
        query = query.lte('check_in_time', dateTo);
      }

      const { data, error } = await query;

      if (error) throw error;
      set({ attendanceRecords: data || [], isLoading: false });
    } catch (error) {
      console.error('Error fetching attendance:', error);
      set({ isLoading: false });
    }
  },

  fetchLastAttendancePerSubscription: async (empresaId: string) => {
    const existing = fetchLastAttendanceInFlight.get(empresaId);
    if (existing) return existing;

    const run = (async () => {
      try {
        const { data, error } = await supabase
          .from('attendance_records')
          .select('id, subscription_id, check_in_time, check_in_method')
          .eq('empresa_id', empresaId)
          .order('check_in_time', { ascending: false })
          .limit(2000);

        if (error) throw error;

        const map: LastAttendanceMap = {};
        for (const row of data || []) {
          const sid = row.subscription_id as string;
          if (!sid || map[sid]) continue;
          map[sid] = {
            check_in_time: row.check_in_time as string,
            check_in_method: (row.check_in_method as AttendanceRecord['check_in_method']) || 'manual',
          };
        }
        set({ lastAttendanceBySubscriptionId: map });
      } catch (error) {
        console.error('Error fetching last attendance per subscription:', error);
        set({ lastAttendanceBySubscriptionId: {} });
      } finally {
        fetchLastAttendanceInFlight.delete(empresaId);
      }
    })();

    fetchLastAttendanceInFlight.set(empresaId, run);
    return run;
  },

  createSubscription: async (data: Partial<MemberSubscription>) => {
    try {
      const { data: newSubscription, error } = await supabase
        .from('member_subscriptions')
        .insert(data)
        .select(
          `
          *,
          cliente:clientes(*),
          producto:productos(*)
        `
        )
        .single();

      if (error) throw error;

      set((state) => ({
        subscriptions: [newSubscription, ...state.subscriptions],
      }));

      return newSubscription;
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'message' in err ? (err as Error).message : String(err);
      const details = err && typeof err === 'object' && 'details' in err ? (err as { details?: string }).details : undefined;
      console.error('Error creating subscription:', message, details ?? '');
      return null;
    }
  },

  updateSubscription: async (id: string, data: Partial<MemberSubscription>) => {
    try {
      const { error } = await supabase
        .from('member_subscriptions')
        .update(data)
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        subscriptions: state.subscriptions.map((sub) =>
          sub.id === id ? { ...sub, ...data } : sub
        ),
      }));
    } catch (error) {
      console.error('Error updating subscription:', error);
    }
  },

  recordAttendance: async (data: Partial<AttendanceRecord>) => {
    try {
      const { data: record, error } = await supabase
        .from('attendance_records')
        .insert(data)
        .select(
          `
          *,
          cliente:clientes(*),
          subscription:member_subscriptions(*)
        `
        )
        .single();

      if (error) throw error;

      // Update classes remaining if applicable.
      // Goes through a SECURITY DEFINER RPC (checkin_decrement_classes) instead of a direct
      // table UPDATE so anon QR check-in does not need a broad UPDATE policy on member_subscriptions.
      const sub = record?.subscription as MemberSubscription | undefined;
      if (sub && sub.classes_remaining !== null) {
        const { data: decremented, error: rpcError } = await supabase.rpc('checkin_decrement_classes', {
          p_subscription_id: record.subscription_id,
          p_empresa_id: record.empresa_id,
        });

        if (rpcError) {
          console.error('Error decrementing classes:', rpcError);
          // Refetch subscription from server to ensure UI is in sync
          await get().fetchSubscriptions(record.empresa_id);
        } else if (decremented === true) {
          // Only update local state if the RPC actually decremented the classes
          set((state) => ({
            subscriptions: state.subscriptions.map((s) =>
              s.id === record.subscription_id
                ? {
                    ...s,
                    classes_remaining: s.classes_remaining! - 1,
                    total_classes_used: (s.total_classes_used || 0) + 1,
                  }
                : s
            ),
          }));
        } else {
          // RPC returned false (authorization failed or update conditions not met)
          // Refetch subscription from server to ensure UI is in sync
          await get().fetchSubscriptions(record.empresa_id);
        }
      }

      set((state) => ({
        attendanceRecords: [record, ...state.attendanceRecords],
      }));

      return record;
    } catch (error) {
      console.error('Error recording attendance:', error);
      return null;
    }
  },

  getSubscriptionByToken: async (token: string) => {
    try {
      const { data, error } = await supabase
        .from('member_subscriptions')
        .select(
          `
          *,
          cliente:clientes(*),
          producto:productos(*),
          empresa:empresas(*)
        `
        )
        .eq('check_in_token', token)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error getting subscription by token:', error);
      return null;
    }
  },

  getActiveSubscriptions: (empresaId: string) => {
    return get().subscriptions.filter(
      (sub) => sub.empresa_id === empresaId && sub.status === 'active'
    );
  },

  getOverdueSubscriptions: (empresaId: string) => {
    const today = new Date().toISOString().split('T')[0] ?? '';
    return get().subscriptions.filter(
      (sub) =>
        sub.empresa_id === empresaId &&
        sub.status === 'active' &&
        (sub.payment_status === 'overdue' ||
          sub.payment_status === 'suspended' ||
          (sub.next_billing_date != null && sub.next_billing_date < today))
    );
  },

  getDueSoonSubscriptions: (empresaId: string) => {
    return get().subscriptions.filter(
      (sub) =>
        sub.empresa_id === empresaId &&
        sub.status === 'active' &&
        sub.payment_status === 'due_soon'
    );
  },

  recordPayment: async (subscriptionId: string, amount: number, paymentMethod: string) => {
    const subscription = get().subscriptions.find((s) => s.id === subscriptionId);
    if (!subscription) throw new Error('Subscription not found');

    const { data: product } = await supabase
      .from('productos')
      .select('duracion_dias')
      .eq('id', subscription.producto_id)
      .single();

    const durationDays = (product?.duracion_dias ?? 30) as number;
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    const currentEnd = subscription.current_period_end
      ? new Date(subscription.current_period_end)
      : today;
    const newPeriodStart = new Date(currentEnd);
    newPeriodStart.setDate(newPeriodStart.getDate() + 1);
    const newPeriodEnd = new Date(newPeriodStart);
    newPeriodEnd.setDate(newPeriodEnd.getDate() + durationDays);
    const newPeriodStartStr = newPeriodStart.toISOString().split('T')[0];
    const newPeriodEndStr = newPeriodEnd.toISOString().split('T')[0];

    const { error: payError } = await supabase.from('subscription_payments').insert({
      empresa_id: subscription.empresa_id,
      subscription_id: subscriptionId,
      cliente_id: subscription.cliente_id,
      amount,
      payment_date: todayStr,
      payment_method: paymentMethod,
      period_start: newPeriodStartStr,
      period_end: newPeriodEndStr,
      status: 'paid',
    });

    if (payError) throw payError;

    const { error: updateError } = await supabase
      .from('member_subscriptions')
      .update({
        current_period_start: newPeriodStartStr,
        current_period_end: newPeriodEndStr,
        next_billing_date: newPeriodEndStr,
        last_payment_date: todayStr,
        payment_status: 'current',
        days_overdue: 0,
        overdue_amount: 0,
        status: 'active',
      })
      .eq('id', subscriptionId);

    if (updateError) throw updateError;

    await get().fetchSubscriptions(subscription.empresa_id);
  },

  getPaymentsByMonth: async (empresaId: string, year: number, month: number) => {
    const key = `${empresaId}:${year}:${month}`;
    const existing = getPaymentsByMonthInFlight.get(key);
    if (existing) return existing;

    const run = (async (): Promise<unknown[]> => {
      try {
        const startDate = new Date(year, month - 1, 1);
        const endDate = new Date(year, month, 0);
        const startStr = startDate.toISOString().split('T')[0];
        const endStr = endDate.toISOString().split('T')[0];

        const { data, error } = await supabase
          .from('subscription_payments')
          .select(
            `
        id,
        amount,
        payment_date,
        payment_method,
        period_start,
        period_end,
        subscription_id,
        cliente_id,
        subscription:member_subscriptions(cliente:clientes(nombre, apellido), producto:productos(nombre))
      `
          )
          .eq('empresa_id', empresaId)
          .gte('payment_date', startStr)
          .lte('payment_date', endStr)
          .order('payment_date', { ascending: false });

        if (error) {
          console.error('Error fetching payments by month:', error);
          return [];
        }
        return data || [];
      } finally {
        getPaymentsByMonthInFlight.delete(key);
      }
    })();

    getPaymentsByMonthInFlight.set(key, run);
    return run;
  },

  updatePaymentStatuses: async (empresaId: string) => {
    const existing = updatePaymentStatusesInFlight.get(empresaId);
    if (existing) return existing;

    const run = (async () => {
      try {
        try {
          await supabase.rpc('update_subscription_payment_status');
        } catch (err) {
          console.error('Error updating payment statuses:', err);
        }
        await get().fetchSubscriptions(empresaId);
      } finally {
        updatePaymentStatusesInFlight.delete(empresaId);
      }
    })();

    updatePaymentStatusesInFlight.set(empresaId, run);
    return run;
  },

  reset: () => {
    set({
      subscriptions: [],
      attendanceRecords: [],
      lastAttendanceBySubscriptionId: {},
      isLoading: false,
    });
  },
}));
